import { useEffect } from 'react';
import type { ChartDataset } from 'chart.js';
import { RefreshCw } from 'lucide-react';
import { ChartBox } from '../components/ChartBox';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import type { PipelineRun, StatusResponse, Tone } from '../types';
import { num } from '../utils';

interface StatusScreenProps {
  data: StatusResponse | null;
  loaded: boolean;
  busy: boolean;
  onReload: () => void;
}

function minutesAgo(iso?: string | null): number | null {
  if (!iso) return null;
  return (Date.now() - new Date(iso).getTime()) / 60000;
}
function agoLabel(min: number | null): string {
  if (min == null) return '—';
  if (min < 1) return 'vừa xong';
  if (min < 60) return `${Math.round(min)} phút trước`;
  if (min < 1440) return `${Math.round(min / 60)} giờ trước`;
  return `${Math.round(min / 1440)} ngày trước`;
}
function dt(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
}

function runSourceLabel(source?: string | null): string {
  if (source === 'cdp-online') return 'Tư vấn online';
  if (source === 'aihub' || source === 'local') return 'Tư vấn tại cửa hàng';
  return 'Nguồn tự động';
}

function ProgressBar({ pct, tone }: { pct: number; tone: string }) {
  return (
    <div className="bar" style={{ height: 12 }}>
      <span style={{ display: 'block', height: '100%', width: `${Math.min(100, Math.max(0, pct))}%`, background: tone, borderRadius: 999 }} />
    </div>
  );
}

export function StatusScreen({ data, loaded, busy, onReload }: StatusScreenProps) {
  // live monitor: auto-refresh every 60s while this tab is open
  useEffect(() => {
    const id = window.setInterval(() => onReload(), 60000);
    return () => window.clearInterval(id);
  }, [onReload]);

  if (busy && !loaded) return <div className="card empty">Đang tải chất lượng dữ liệu...</div>;
  const hb = data?.heartbeat || null;
  const cov = data?.coverage || {};
  const runs: PipelineRun[] = data?.runs || [];

  const hbAge = minutesAgo(hb?.last_run_at);
  // launchd cadence is 30 min; healthy if heartbeat < 35 min, warn < 90, else stale
  const schedTone: Tone = hbAge == null ? 'gray' : hbAge < 35 ? 'green' : hbAge < 90 ? 'amber' : 'red';
  const schedText = hbAge == null ? 'Chưa có heartbeat' : hbAge < 35 ? 'Đang chạy đúng lịch' : hbAge < 90 ? 'Trễ nhịp' : 'Có thể đã dừng';
  const toneHex: Record<string, string> = { green: '#059669', amber: '#d97706', red: '#d71920', blue: '#2563eb', gray: '#94a3b8' };

  const since = Number(cov.captures_since || 0);
  const sinceDone = Number(cov.enriched_since || 0);
  const sincePct = since ? Math.round((sinceDone / since) * 100) : 0;
  const total = Number(cov.captures_total || 0);
  const totalDone = Number(cov.enriched_total || 0);
  const totalPct = total ? Math.round((totalDone / total) * 100) : 0;

  const latestEnrichAge = minutesAgo(cov.latest_enriched_at);
  const chron = [...runs].reverse(); // oldest -> newest for the trend chart
  const onl = data?.online || {};
  const onlineAge = onl.latest_enriched_at ? minutesAgo(onl.latest_enriched_at) : null;
  const onlineTone: Tone = onlineAge == null ? 'gray' : onlineAge < 26 * 60 ? 'green' : onlineAge < 50 * 60 ? 'amber' : 'red';
  const outboxDead = Number((onl.outbox || []).find(o => o.status === 'dead')?.n || 0);
  const src = data?.source || {};
  // CDP đổ theo lô 1 lần/ngày (15h–20h VN), phủ chat tới ~14h cùng ngày — nên trễ tới ~30h là bình thường.
  const srcTone: Tone = src.lag_hours == null ? 'gray' : src.lag_hours <= 30 ? 'green' : src.lag_hours <= 48 ? 'amber' : 'red';
  // Trễ thấp vẫn có thể che một lỗ ở giữa lô: 18/09 lô chỉ phủ 11:17–14:01, bỏ trắng 21 giờ trước đó.
  // Nên đo cả TỈ LỆ PHỦ 24h, không chỉ mốc mới nhất.
  const rawRows24h = Number(src.raw_rows_24h || 0);
  const cdpRows24h = Number(src.cdp_rows_24h || 0);
  const coverPct = rawRows24h ? Math.round((cdpRows24h / rawRows24h) * 100) : null;
  const coverTone: Tone = coverPct == null ? 'gray' : coverPct >= 60 ? 'green' : coverPct >= 20 ? 'amber' : 'red';


  return (
    <>
      <section className="grid kpis">
        <KpiCard label="Lần cập nhật gần nhất" value={agoLabel(hbAge)} sub={schedText} tone={schedTone} />
        <KpiCard label="Đã xử lý từ 11/06" value={`${sincePct}%`} sub={`${num(sinceDone)}/${num(since)} lượt`} tone={sincePct >= 100 ? 'green' : 'amber'} />
        <KpiCard label="Tổng đã phân loại" value={num(totalDone)} sub={`trên ${num(total)} lượt có nội dung`} tone="blue" />
        <KpiCard label="Chưa rõ ngành hàng" value={cov.unknown_pct != null ? `${cov.unknown_pct}%` : '—'} sub="Tỷ lệ chưa phân loại" tone={Number(cov.unknown_pct) > 10 ? 'amber' : 'green'} />
        <KpiCard label="Độ tin cậy trung bình" value={cov.avg_confidence != null ? Number(cov.avg_confidence).toFixed(3) : '—'} sub="Mức chắc chắn khi phân loại" tone="violet" />
        <KpiCard label="Dữ liệu mới nhất" value={agoLabel(latestEnrichAge)} sub={dt(cov.latest_enriched_at)} tone="cyan" />
      </section>

      <section className="card space">
        <div className="section-title"><h2>Tư vấn online (CDP)</h2><span>Nguồn CDP đổ theo lô 1 lần/ngày</span></div>
        <div className="grid kpis">
          <KpiCard label="Dữ liệu chat tới" value={onl.latest_start_vn ? onl.latest_start_vn.replace(/^(\d{4})-(\d{2})-(\d{2}) /, '$3/$2 ') : '—'} sub={onlineAge == null ? 'Chưa có dữ liệu' : onlineAge < 26 * 60 ? 'Trong nhịp lô hằng ngày' : 'Nguồn CDP trễ hơn 1 ngày'} tone={onlineTone} />
          <KpiCard label="Kho cập nhật" value={agoLabel(minutesAgo(onl.latest_enriched_at))} sub={dt(onl.latest_enriched_at)} tone="cyan" />
          <KpiCard label="Hội thoại 7 ngày" value={num(onl.rows_7d)} sub={`tổng ${num(onl.rows_total)} hội thoại`} tone="blue" />
          <KpiCard label="Cảnh báo realtime" value={num(outboxDead)} sub={outboxDead ? 'gói chết 7 ngày qua — kiểm tra n8n' : 'không có gói chết'} tone={outboxDead ? 'red' : 'green'} />
          <KpiCard label="Nguồn CDP mới nhất" value={src.cdp_max_vn ? src.cdp_max_vn.replace(/^(\d{4})-(\d{2})-(\d{2}) /, '$3/$2 ') : '—'} sub={src.lag_hours == null ? 'Không đọc được nguồn' : `trễ ${src.lag_hours} giờ so với hiện tại`} tone={srcTone} />
          <KpiCard label="CDP phủ 24 giờ" value={coverPct == null ? '—' : `${coverPct}%`} sub={`${num(cdpRows24h)} / ${num(rawRows24h)} chat thô · thô tới ${src.raw_max_vn ? src.raw_max_vn.slice(11) : '—'}`} tone={coverTone} />
        </div>
        <div className="chart" style={{ marginTop: 12 }}>
          <ChartBox labels={(onl.days || []).map(d => d.day.slice(8) + '/' + d.day.slice(5, 7))} datasets={[{ label: 'Hội thoại / ngày (giờ VN)', data: (onl.days || []).map(d => Number(d.n || 0)), type: 'bar', backgroundColor: '#0891b2cc', borderColor: '#0891b2', borderWidth: 1, borderRadius: 5 } as unknown as ChartDataset]} />
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Tình trạng cập nhật tự động</h2>
          <button className="btn live" type="button" onClick={onReload} disabled={busy}><RefreshCw size={14} />{busy ? 'Đang đọc...' : 'Tải lại'}</button>
        </div>
        <div className="grid split">
          <div>
            <div className="kpi-label">Lần cập nhật gần nhất</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0' }}>
              <Tag tone={schedTone}>{schedText}</Tag>
              <span className="muted">{dt(hb?.last_run_at)} · {hb?.mode || '—'} · {hb?.host || '—'}</span>
            </div>
            <div className="muted">Màu xanh nghĩa là dữ liệu vẫn được cập nhật đúng lịch. Quá 35 phút cần kiểm tra hệ thống cập nhật.</div>
          </div>
          <div>
            <div className="kpi-label">Độ đầy đủ</div>
            <div style={{ margin: '8px 0 4px' }}>Dữ liệu từ 11/06: <b>{sinceDone}/{since}</b> ({sincePct}%)</div>
            <ProgressBar pct={sincePct} tone={toneHex[sincePct >= 100 ? 'green' : 'amber']} />
            <div style={{ margin: '12px 0 4px' }}>Toàn bộ: <b>{totalDone}/{total}</b> ({totalPct}%)</div>
            <ProgressBar pct={totalPct} tone={toneHex.blue} />
          </div>
        </div>
      </section>

      <section className="card space">
        <div className="section-title"><h2>Chất lượng qua các lần chạy</h2><span>{runs.length} lần gần nhất</span></div>
        <div className="chart">
          <ChartBox
            labels={chron.map(r => dt(r.started_at))}
            datasets={[
              { label: 'Số lượt đã xử lý', data: chron.map(r => Number(r.enriched_count || 0)), type: 'bar', backgroundColor: '#2563ebcc', borderColor: '#2563eb', borderWidth: 1, borderRadius: 5, yAxisID: 'y' } as unknown as ChartDataset,
              { label: 'Chưa rõ %', data: chron.map(r => Number(r.unknown_cat_pct || 0)), type: 'line', borderColor: '#d71920', backgroundColor: '#d7192022', borderWidth: 2, tension: 0.35, pointRadius: 3, yAxisID: 'y2' } as unknown as ChartDataset,
              { label: 'Độ tin cậy %', data: chron.map(r => Math.round(Number(r.avg_confidence || 0) * 100)), type: 'line', borderColor: '#059669', backgroundColor: '#05966922', borderWidth: 2, tension: 0.35, pointRadius: 3, yAxisID: 'y2' } as unknown as ChartDataset,
            ]}
            options={{ scales: { y: { position: 'left', beginAtZero: true, title: { display: true, text: 'dòng' } }, y2: { position: 'right', beginAtZero: true, suggestedMax: 100, grid: { drawOnChartArea: false }, title: { display: true, text: '%' } } } }}
          />
        </div>
      </section>

      <section className="card space">
        <div className="section-title"><h2>Lịch sử cập nhật</h2><span>{runs.length} lần gần nhất</span></div>
        <div className="scroll">
          <table>
            <thead><tr><th>Lần cập nhật</th><th>Bắt đầu</th><th>Nguồn</th><th className="right">Dữ liệu vào</th><th className="right">Đã xử lý</th><th className="right">Bỏ qua</th><th className="right">Lỗi</th><th className="right">Chưa rõ %</th><th className="right">Tin cậy</th><th className="right">Thời gian</th></tr></thead>
            <tbody>
              {runs.length === 0 ? (
                <tr><td colSpan={10} className="muted">Chưa có lịch sử cập nhật.</td></tr>
              ) : runs.map(r => (
                <tr key={r.run_id}>
                  <td className="tnum">{r.run_id}</td>
                  <td className="tnum">{dt(r.started_at)}</td>
                  <td><Tag tone={r.source === 'aihub' ? 'violet' : 'gray'}>{runSourceLabel(r.source)}</Tag></td>
                  <td className="right tnum">{num(r.input_count)}</td>
                  <td className="right tnum"><b>{num(r.enriched_count)}</b></td>
                  <td className="right tnum">{num(r.skipped_count)}</td>
                  <td className="right tnum">{r.failed_count ? <Tag tone="red">{r.failed_count}</Tag> : '0'}</td>
                  <td className="right tnum">{r.unknown_cat_pct != null ? `${r.unknown_cat_pct}%` : '—'}</td>
                  <td className="right tnum">{r.avg_confidence != null ? Number(r.avg_confidence).toFixed(3) : '—'}</td>
                  <td className="right tnum">{r.metrics?.elapsed_s ? `${Math.round(r.metrics.elapsed_s)}s` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <details className="tech-details space">
        <summary>Thông tin kỹ thuật</summary>
        Luồng xử lý: lấy dữ liệu → phân loại nội dung → cập nhật kho báo cáo. Hệ thống chạy mỗi 30 phút và lưu lịch sử để phục vụ kiểm tra khi có sự cố. Nhánh tư vấn online đọc từ CDP (conversation_analysis) theo lô hằng ngày; giờ hiển thị là giờ Việt Nam. 'Nguồn CDP mới nhất' đọc trực tiếp từ cdp.conversation_analysis, còn 'Chat thô' từ raw_customer_service.caresoft_chat — trễ quá 30 giờ nghĩa là job phân tích phía CDP đang đứng, không phải pipeline của dashboard.
      </details>
    </>
  );
}
